import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Euro, Plus, ShoppingBag, UserPlus } from 'lucide-react';
import type { DashboardOverview } from '@rosita/shared';
import { SEO } from '../../seo/useSEO';
import { t } from '../../i18n/pt';
import { api } from '../../lib/api';
import { formatEur, formatLongDatePT } from '../../lib/format';
import { useAuthStore } from '../../stores/authStore';
import { KpiCard } from '../../components/admin/KpiCard';
import { SectionCard } from '../../components/admin/SectionCard';
import { SalesChart } from '../../components/admin/SalesChart';
import { OrderStatusBadge } from '../../components/admin/OrderStatusBadge';

export default function DashboardPage(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const [data, setData] = useState<DashboardOverview | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const response = await api.get<DashboardOverview>('/admin/dashboard/stats');
        setData(response.data);
      } catch {
        setError(true);
      }
    })();
  }, []);

  const s = t.admin.sections;

  return (
    <>
      <SEO title={t.admin.dashboard} noindex />

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1>
            {t.admin.greeting},{' '}
            <span className="text-admin-accent">{user?.firstName ?? ''}</span>
          </h1>
          <p className="mt-1 text-sm text-admin-text-soft">{formatLongDatePT()}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/produtos/novo"
            className="inline-flex items-center gap-2 rounded-admin bg-admin-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-admin-primary-hover"
          >
            <Plus aria-hidden className="h-4 w-4" />
            {t.admin.actions.addProduct}
          </Link>
          <Link
            to="/admin/encomendas"
            className="inline-flex items-center gap-2 rounded-admin border border-admin-border bg-admin-surface px-4 py-2 text-sm font-medium text-admin-text transition-colors hover:bg-admin-surface-alt"
          >
            <ShoppingBag aria-hidden className="h-4 w-4" />
            {t.admin.actions.viewOrders}
          </Link>
        </div>
      </div>

      <div className="mt-3">
        <span className="inline-flex items-center rounded-admin border border-admin-border bg-admin-surface px-3 py-1.5 text-sm text-admin-text-soft">
          {t.admin.weekly}
        </span>
      </div>

      {error ? (
        <p className="mt-6 text-admin-danger">{t.errors.generic}</p>
      ) : !data ? (
        <p className="mt-6 text-admin-text-soft">{t.common.loading}</p>
      ) : (
        <>
          {/* KPI row */}
          <div className="mt-6 grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(200px,1fr))]">
            <KpiCard
              icon={Euro}
              label={t.admin.kpis.totalSales}
              value={formatEur(data.kpis.totalSales.value)}
              deltaPct={data.kpis.totalSales.deltaPct}
              deltaSuffix={t.admin.kpis.vsLastWeek}
            />
            <KpiCard
              icon={ShoppingBag}
              label={t.admin.kpis.ordersReceived}
              value={data.kpis.ordersReceived.value}
              deltaPct={data.kpis.ordersReceived.deltaPct}
              deltaSuffix={t.admin.kpis.vsLastWeek}
            />
            <KpiCard
              icon={Clock}
              label={t.admin.kpis.pendingOrders}
              value={String(data.kpis.pendingOrders.value)}
              note={t.admin.kpis.needsAttention}
            />
            <KpiCard
              icon={UserPlus}
              label={t.admin.kpis.newCustomers}
              value={data.kpis.newCustomers.value}
              deltaPct={data.kpis.newCustomers.deltaPct}
              deltaSuffix={t.admin.kpis.vsLastWeek}
            />
          </div>

          {/* Chart + recent orders */}
          <div className="mt-6 grid gap-4 lg:[grid-template-columns:1.6fr_1fr]">
            <SectionCard title={s.salesWeek}>
              <SalesChart
                days={data.salesSeries.days}
                thisWeek={data.salesSeries.thisWeek}
                lastWeek={data.salesSeries.lastWeek}
              />
            </SectionCard>

            <SectionCard
              title={s.recentOrders}
              action={{ label: s.viewAll, to: '/admin/encomendas' }}
            >
              {data.recentOrders.length === 0 ? (
                <p className="text-sm text-admin-text-soft">{s.empty}</p>
              ) : (
                <ul className="flex flex-col">
                  {data.recentOrders.map((o) => (
                    <li
                      key={o.orderNumber}
                      className="flex items-center gap-3 border-b border-admin-border py-3 last:border-b-0"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-admin-surface-alt text-sm font-semibold text-admin-primary">
                        {o.customerName.charAt(0).toUpperCase() || '?'}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-admin-text">
                          {o.customerName}
                        </div>
                        <div className="truncate text-xs text-admin-text-soft">
                          {o.itemsSummary}
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1">
                        <span className="text-sm font-semibold text-admin-text">
                          {formatEur(o.total)}
                        </span>
                        <OrderStatusBadge status={o.status} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>
          </div>

          {/* Bottom row */}
          <div className="mt-6 grid gap-4 lg:grid-cols-2">
            <SectionCard
              title={s.topProducts}
              action={{ label: s.viewProducts, to: '/admin/produtos' }}
            >
              {data.topProducts.length === 0 ? (
                <p className="text-sm text-admin-text-soft">{s.empty}</p>
              ) : (
                <ul className="flex flex-col">
                  {data.topProducts.map((p, i) => (
                    <li
                      key={p.productId ?? `${p.name}-${i}`}
                      className="flex items-center gap-3 border-b border-admin-border py-3 last:border-b-0"
                    >
                      <span className="w-4 shrink-0 text-sm font-semibold text-admin-muted">
                        {i + 1}
                      </span>
                      {p.thumbUrl ? (
                        <img
                          src={p.thumbUrl}
                          alt=""
                          className="h-10 w-10 shrink-0 rounded-admin object-cover"
                        />
                      ) : (
                        <span className="h-10 w-10 shrink-0 rounded-admin bg-gradient-to-br from-admin-blush to-admin-accent" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-admin-text">
                          {p.name}
                        </div>
                        <div className="text-xs text-admin-text-soft">
                          {p.unitsSold} {s.unitsSold}
                        </div>
                      </div>
                      <span className="shrink-0 text-sm font-medium text-admin-text">
                        {formatEur(p.price)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>

            <SectionCard
              title={s.customers}
              action={{ label: s.viewCustomers, to: '/admin/clientes' }}
            >
              <dl className="flex flex-col">
                <StatRow label={s.totalCustomers} value={String(data.customers.total)} />
                <StatRow label={s.newThisMonth} value={String(data.customers.newThisMonth)} />
                <StatRow label={s.returning} value={String(data.customers.returning)} />
              </dl>
              <div className="mt-4">
                <div className="flex items-center justify-between text-xs text-admin-text-soft">
                  <span>{s.retentionRate}</span>
                  <span>{Math.round(data.customers.retentionRate * 100)}%</span>
                </div>
                <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-admin-surface-alt">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-admin-accent to-admin-primary"
                    style={{ width: `${Math.round(data.customers.retentionRate * 100)}%` }}
                  />
                </div>
              </div>
            </SectionCard>

          </div>
        </>
      )}
    </>
  );
}

function StatRow({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-center justify-between border-b border-admin-border py-3 last:border-b-0">
      <dt className="text-sm text-admin-text-soft">{label}</dt>
      <dd className="text-lg font-semibold text-admin-text">{value}</dd>
    </div>
  );
}
